import React, { useState } from 'react';
import { Box, InputBase, Menu, MenuItem, Typography } from '@material-ui/core';
import { ExpandMore, Search as SearchIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

export interface CommunicationsFilterOption {
  label: string;
  value: string;
}

export interface CommunicationsFilter {
  key: string;
  options: CommunicationsFilterOption[];
  value: string;
  onChange: (value: string) => void;
}

export interface CommunicationsDateRange {
  from: string | null;
  to: string | null;
  label: string;
  onChange: (from: string | null, to: string | null) => void;
}

interface CommunicationsToolbarProps {
  title: string;
  search: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  filters?: CommunicationsFilter[];
  dateRange?: CommunicationsDateRange;
}

// Matches the responses table in forms and surveys: blank loads everything, paginated.
const dateInputStyle: React.CSSProperties = {
  color: '#646464',
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  padding: '5px 8px',
  fontSize: 12,
  fontFamily: 'inherit',
  backgroundColor: 'transparent',
  outline: 'none',
};

const filterButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#646464',
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  marginRight: 8,
};

const FilterMenu = ({ filter }: { filter: CommunicationsFilter }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selected = filter.options.find((option) => option.value === filter.value) ?? filter.options[0];

  return (
    <>
      <TabletButton
        size='small'
        endIcon={<ExpandMore style={{ fontSize: 14 }} />}
        style={filterButtonStyle}
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
      >
        {selected.label}
      </TabletButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} keepMounted={false}>
        {filter.options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === filter.value}
            style={{ fontSize: 13 }}
            onClick={() => {
              filter.onChange(option.value);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const CommunicationsToolbar = ({
  title,
  search,
  searchPlaceholder,
  onSearchChange,
  onSearch,
  filters = [],
  dateRange,
}: CommunicationsToolbarProps) => {
  const { isMobile, isSm } = useCampfireTheme();

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      padding='16px 28px'
      borderBottom='1px solid #EFEFEF'
      flexShrink={0}
    >
      {!isMobile && !isSm && (
        <Typography style={{ fontSize: '14px', fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      )}

      <Box display='flex' alignItems='center'>
        {dateRange && (
          <Box display='flex' alignItems='center' style={{ gap: 6, marginRight: 8 }}>
            <input
              type='date'
              aria-label={`${dateRange.label} from date`}
              style={dateInputStyle}
              value={dateRange.from ?? ''}
              max={dateRange.to ?? undefined}
              onChange={(e) => dateRange.onChange(e.target.value || null, dateRange.to)}
            />
            <Typography style={{ fontSize: 12, color: '#646464' }}>&ndash;</Typography>
            <input
              type='date'
              aria-label={`${dateRange.label} to date`}
              style={dateInputStyle}
              value={dateRange.to ?? ''}
              min={dateRange.from ?? undefined}
              onChange={(e) => dateRange.onChange(dateRange.from, e.target.value || null)}
            />
            {(dateRange.from || dateRange.to) && (
              <TabletButton
                size='small'
                style={{ ...filterButtonStyle, marginRight: 0 }}
                onClick={() => dateRange.onChange(null, null)}
              >
                Clear
              </TabletButton>
            )}
          </Box>
        )}

        {filters.map((filter) => (
          <FilterMenu key={filter.key} filter={filter} />
        ))}

        <Box
          display='flex'
          alignItems='center'
          width={260}
          height={32}
          paddingX='12px'
          style={{ border: '0.5px solid #d9d9d9', borderRadius: 4, boxSizing: 'border-box' }}
        >
          <SearchIcon style={{ fontSize: 16, color: '#646464', flexShrink: 0, marginRight: 8 }} />
          <InputBase
            value={search}
            placeholder={searchPlaceholder}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            onBlur={onSearch}
            style={{ flexGrow: 1, fontSize: 13, color: '#444444' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
